import { useGenerateMasterPrompt, useGetAiWorkspace } from "@entities/aiWorkspace";
import { ToastService } from "@shared/lib/services";
import { DisabledPopover } from "@shared/ui/DisabledPopover";
import { Check, FileUp, Loader2, Wand2, X } from "lucide-react";
import React, { useEffect, useId, useRef, useState } from "react";
import { PromptGuide } from "../PromptGuide";
import { VetPromptModule } from "../promptGuides";
import { extractPdfText } from "@shared/ui/Editor/lib/briefImportPdf";
import frameStyles from "../ScriptGenerationTab/scriptFrame.module.scss";
import { promptToText } from "./promptToText";
import styles from "./styles.module.scss";

type WorkspacePromptEditorProps = {
  savedPrompt: string;
  onSave?: (prompt: string) => void;
  isSaving: boolean;
  saveVersion?: number;
  label?: string;
  hint?: string;
  readOnly?: boolean;
  /** Edits stay on the request. They are not saved as the workspace master prompt. */
  requestOnly?: boolean;
  /** Prompt last saved on the request. Defaults to savedPrompt. */
  committedPrompt?: string;
  contentKey?: string;
  moduleId?: VetPromptModule;
  aiWorkspaceId?: string;
  hasPriorResults?: boolean;
  priorResultNoun?: string;
  onHtmlChange?: (html: string) => void;
};

export function WorkspacePromptEditor({
  savedPrompt,
  onSave,
  isSaving,
  saveVersion = 0,
  label = "Master prompt",
  hint,
  readOnly = false,
  requestOnly = false,
  committedPrompt,
  contentKey,
  moduleId,
  aiWorkspaceId,
  hasPriorResults = false,
  priorResultNoun = "checks",
  onHtmlChange,
}: WorkspacePromptEditorProps) {
  const fieldId = useId();
  const pendingPrompt = useRef("");
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState(() => promptToText(savedPrompt));
  const [savedFlash, setSavedFlash] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isExtractingPdf, setIsExtractingPdf] = useState(false);
  const committed = promptToText(committedPrompt ?? savedPrompt);
  const dirty = !readOnly && draft !== committed;
  const showSave = !readOnly && (!requestOnly || Boolean(onSave));

  const onChangeRef = useRef(onHtmlChange);
  onChangeRef.current = onHtmlChange;

  useEffect(() => {
    const text = promptToText(savedPrompt);
    setDraft(text);
    onChangeRef.current?.(text);
  }, [contentKey, savedPrompt]);

  useEffect(() => {
    if (!saveVersion) return;
    setSavedFlash(true);
    const timer = window.setTimeout(() => setSavedFlash(false), 2000);
    return () => window.clearTimeout(timer);
  }, [saveVersion]);

  const publishText = (value: string) => {
    setDraft(value);
    onHtmlChange?.(value);
    return value;
  };

  const { data: workspaceData } = useGetAiWorkspace(aiWorkspaceId || "");
  const overviewMissing = !promptToText(workspaceData?.data?.overview || "").trim();
  const { generate, isGenerating } = useGenerateMasterPrompt((analysis) => {
    publishText(analysis);
  });

  const handlePdfUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsExtractingPdf(true);
    try {
      const text = await extractPdfText(file);
      publishText(text);
      ToastService.success("Prompt imported from PDF");
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to extract text from PDF";
      ToastService.error(message);
    } finally {
      setIsExtractingPdf(false);
      if (pdfInputRef.current) {
        pdfInputRef.current.value = "";
      }
    }
  };

  const requestSave = () => {
    if (!requestOnly && hasPriorResults) {
      pendingPrompt.current = draft;
      setConfirmOpen(true);
      return;
    }
    onSave?.(draft);
  };

  const guide = moduleId && !readOnly ? <PromptGuide moduleId={moduleId} /> : null;

  return (
    <div className={styles.editorContainer}>
      <div className={styles.editorHeader}>
        <div className={styles.labelGroup}>
          <label className={styles.editorLabel} htmlFor={fieldId}>
            {label}
          </label>
          {guide}
        </div>
        {!readOnly ? (
          <div className={styles.headerActions}>
            <input
              ref={pdfInputRef}
              type="file"
              accept=".pdf,application/pdf"
              style={{ display: "none" }}
              onChange={handlePdfUpload}
            />
            <button
              type="button"
              className={styles.importPdfBtn}
              disabled={isExtractingPdf}
              title="Import prompt text from a PDF file"
              onClick={() => pdfInputRef.current?.click()}
            >
              {isExtractingPdf ? (
                <Loader2 size={14} className="spin-animation" />
              ) : (
                <FileUp size={14} />
              )}
              {isExtractingPdf ? "Importing…" : "Import from PDF"}
            </button>
            {moduleId && aiWorkspaceId ? (
              <DisabledPopover
                isDisabled={overviewMissing}
                content="Add a master brief before generating from the overview."
              >
                <button
                  type="button"
                  className={styles.generateBtn}
                  disabled={isGenerating || readOnly || overviewMissing}
                  title={overviewMissing ? undefined : "Uses the saved workspace overview."}
                  onClick={() => {
                    if (overviewMissing) return;
                    generate({ aiWorkspaceId, type: moduleId });
                  }}
                >
                  {isGenerating ? <Loader2 size={14} className="spin-animation" /> : <Wand2 size={14} />}
                  Generate from overview
                </button>
              </DisabledPopover>
            ) : null}
          </div>
        ) : null}
      </div>
      {hint ? <p className={styles.hint}>{hint}</p> : null}
      <div
        className={
          isGenerating
            ? `${frameStyles.scriptFrameGenerating} ${styles.promptGenerating}`
            : undefined
        }
      >
        <div>
          <textarea
            id={fieldId}
            className={styles.promptInput}
            value={draft}
            readOnly={readOnly || isGenerating}
            rows={8}
            placeholder="Describe how the AI should evaluate checks for this module…"
            onChange={(event) => publishText(event.target.value)}
          />
        </div>
      </div>
      {showSave ? (
        <div className={styles.actionRow}>
          <button
            type="button"
            className={requestOnly ? styles.requestSaveBtn : styles.savePromptBtn}
            disabled={!dirty || isSaving}
            onClick={requestSave}
          >
            {isSaving ? (
              <Loader2 size={14} className="spin-animation" />
            ) : savedFlash ? (
              <Check size={14} />
            ) : null}
            {savedFlash && !dirty
              ? "Saved"
              : requestOnly
                ? "Save request prompt"
                : "Save master prompt"}
          </button>
          {!dirty && committed ? (
            <span className={styles.savedStatus}>
              {requestOnly
                ? "Saved — this check uses this prompt"
                : "Saved — checks use this rubric"}
            </span>
          ) : null}
          {dirty ? <span className={styles.unsavedStatus}>Unsaved changes</span> : null}
        </div>
      ) : null}
      {!requestOnly && confirmOpen ? (
        <div className={styles.backdrop} onClick={() => setConfirmOpen(false)}>
          <div
            className={styles.dialog}
            role="dialog"
            aria-modal="true"
            onClick={(event) => event.stopPropagation()}
          >
            <div className={styles.dialogHeader}>
              <div>
                <h3 className={styles.dialogTitle}>Save master prompt?</h3>
                <p className={styles.dialogCopy}>
                  New {priorResultNoun} start from this master prompt. Checks already created
                  keep the request prompt they were given and are not marked outdated.
                </p>
              </div>
              <button
                type="button"
                className={styles.closeBtn}
                aria-label="Close"
                onClick={() => setConfirmOpen(false)}
              >
                <X size={18} />
              </button>
            </div>
            <div className={styles.dialogActions}>
              <button type="button" className={styles.cancelBtn} onClick={() => setConfirmOpen(false)}>
                Cancel
              </button>
              <button
                type="button"
                className={styles.confirmBtn}
                onClick={() => {
                  setConfirmOpen(false);
                  onSave?.(pendingPrompt.current);
                }}
              >
                Save prompt
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
